/**
 * Main application component with route definitions.
 *
 * This is the React Router equivalent of the AngularJS ngRoute configuration
 * found in brew.js and all the sub-module _module.js files.
 *
 * Route structure mirrors the legacy app exactly:
 *   /login                                    - Auth (public; signup and password reset are owned by brewauth's /ui/signup, /ui/reset)
 *   /dashboard, /dashboard/discover, /dashboard/build - Dashboard (protected)
 *   /build/:modelId                          - Model builder (protected)
 *   /learn, /learn/help/functionals/...      - Learn / Functional reference (protected)
 *   /develop, /develop/publish, /develop/view/:jarId - Developer (role-gated)
 *   /users/profile/:id/:context              - User profile (protected)
 *   /admin/...                               - Admin section (admin role)
 *   /tasks/:taskId                           - Task viewer (protected)
 *   /groups, /groups/:id                     - Groups workspace (protected); /admin/groups/* redirect here
 *   /401, /403, /404, /500, /503             - Error pages
 *
 * The legacy app used hash-based routing (#/path). This React app uses
 * HashRouter to maintain backwards compatibility with existing bookmarks
 * and server configuration.
 */
import { lazy } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router';
import { useSuppressAutofill } from '@/hooks/useSuppressAutofill';
import { useAuth } from '@/auth/useAuth';
import RouteSuspense from '@/components/common/RouteSuspense';

// Layout
import AppLayout from '@/components/layout/AppLayout';
import StandaloneLayout from '@/components/layout/StandaloneLayout';
import ProtectedRoute from '@/components/layout/ProtectedRoute';

// Auth pages
import LoginPage from '@/pages/auth/LoginPage';

// Dashboard pages
import DashboardPage from '@/pages/dashboard/DashboardPage';
import ExternalRedirectPage from '@/pages/dashboard/ExternalRedirectPage';

// Main Discover page (Editorial layout)
import DiscoverV1Editorial from '@/pages/dashboard/DiscoverVariants/DiscoverV1Editorial';

// Build (model editor) — lazy-loaded; pulls in Konva + the entire src/canvas tree,
// which we don't want in the main bundle.
const ModelBuilderPage = lazy(() => import('@/pages/build/ModelBuilderPage'));
// Mobile run-view gate — lazy so the mobile bundle doesn't bloat the desktop path.
const RunViewGate = lazy(() => import('@/components/mobile/RunViewGate'));
import ModelForbiddenPage from '@/pages/build/ModelForbiddenPage';

// The pages below are lazy too: none of them is on the landing path (login,
// dashboard, Discover), so they load on first visit instead of with every
// first paint. ProfilePage alone pulled amCharts5 into the eager bundle via
// the BrewAI usage charts.

// Viz
const VizPage = lazy(() => import('@/pages/viz/VizPage'));

// BrewDB — lazy-loaded so AG-Grid + the workbench tree don't ship
// in the eager bundle. The ag-grid vendor chunk only downloads when
// the user navigates to /brewdb (or the popped-out results route).
const BrewDBPage = lazy(() => import('@/pages/brewdb/BrewDBPage'));
const BrewDBResultsPage = lazy(() => import('@/pages/brewdb/BrewDBResultsPage'));

// Learn / Help
const LearnPage = lazy(() => import('@/pages/learn/LearnPage'));
const HelpFunctionalsPage = lazy(() => import('@/pages/learn/HelpFunctionalsPage'));
const HelpFunctionalDetailPage = lazy(() => import('@/pages/learn/HelpFunctionalDetailPage'));
// HelpModelDetailPage pulls in amCharts5 (~381KB) via AuditHistogramChart;
// keep it out of the eager preload set.
const HelpModelDetailPage = lazy(() => import('@/pages/learn/HelpModelDetailPage'));

// Develop
const DevelopPublishPage = lazy(() => import('@/pages/develop/DevelopPublishPage'));
const DevelopViewPage = lazy(() => import('@/pages/develop/DevelopViewPage'));

// Users
const ProfilePage = lazy(() => import('@/pages/users/ProfilePage'));
// ResourceViewPage pulls in amCharts5 (~381KB) via AuditHistogramChart; lazy.
const ResourceViewPage = lazy(() => import('@/pages/users/ResourceViewPage'));

// Automation — TaskViewPage pulls in amCharts5 via TaskMetricsTab; lazy.
const TaskViewPage = lazy(() => import('@/pages/automation/TaskViewPage'));

// Standalone output viewers (open in a new tab from OutputProperty).
// TableViewerPage statically imports GeoOutputViewer → Leaflet (~293KB),
// so keep it lazy to avoid pulling Leaflet into the eager preload.
const TableViewerPage = lazy(() => import('@/pages/table/TableViewerPage'));
const OutputViewerPage = lazy(() => import('@/pages/output/OutputViewerPage'));

// Admin (lazy-loaded — split into a separate bundle from the main app)
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage'));
const AdminConfigurationsPage = lazy(() => import('@/pages/admin/AdminConfigurationsPage'));
const AdminProfilesPage = lazy(() => import('@/pages/admin/AdminProfilesPage'));
const AdminModelsPage = lazy(() => import('@/pages/admin/AdminModelsPage'));
const AdminLogsPage = lazy(() => import('@/pages/admin/AdminLogsPage'));
const AdminResourcesPage = lazy(() => import('@/pages/admin/AdminResourcesPage'));
const AdminJobsPage = lazy(() => import('@/pages/admin/AdminJobsPage'));
const AdminTasksPage = lazy(() => import('@/pages/admin/AdminTasksPage'));
const AdminTaskDetailPage = lazy(() => import('@/pages/admin/AdminTaskDetailPage'));
const AdminBrewmastersPage = lazy(() => import('@/pages/admin/AdminBrewmastersPage'));
const AdminSearchPage = lazy(() => import('@/pages/admin/AdminSearchPage'));
const AdminSessionsPage = lazy(() => import('@/pages/admin/AdminSessionsPage'));
const LifecycleJobDetailRoute = lazy(() => import('@/pages/admin/AdminProfilesPage/LifecycleJobDetailRoute'));
const AdminRegistrationPage = lazy(() => import('@/pages/admin/AdminRegistrationPage'));
const AdminBrewAiInsightsPage = lazy(() => import('@/pages/admin/AdminBrewAiInsightsPage'));
const AdminSetupPage = lazy(() => import('@/pages/admin/AdminSetupPage'));
const AdminCredentialsPage = lazy(() => import('@/pages/admin/AdminCredentialsPage'));
const AdminEmailTemplatesPage = lazy(() => import('@/pages/admin/AdminEmailTemplatesPage'));

// Groups workspace (jorigin/brewauth#29 GB1) — lazy, like the admin pages.
// Its routes, and the redirects from the retired /admin/groups URLs, live in
// groupsRoutes.
import { groupsRoutes } from '@/pages/groups/groupsRoutes';
const GroupsWorkspace = lazy(() => import('@/pages/groups/GroupsWorkspace'));

// Error pages
import ErrorPage from '@/pages/errors/ErrorPage';

/**
 * Renders the admin view of a profile for admins; redirects non-admins to
 * the public profile route so they get the guest view instead of a 403.
 */
function AdminProfileDetailRoute() {
  const { userProfile } = useAuth();
  const { id, context } = useParams<{ id: string; context?: string }>();

  if (!userProfile?.$isAdmin) {
    return <Navigate to={`/users/profile/${id}/${context || 'info'}`} replace />;
  }
  if (!context) {
    return <Navigate to="info" replace />;
  }
  return (
    <RouteSuspense>
      <AdminLayout>
        <ProfilePage viewMode="admin" />
      </AdminLayout>
    </RouteSuspense>
  );
}

export default function App() {
  // Stop browser credit-card/address autofill from attaching to plain
  // text/search fields (search boxes, filters) app-wide. See the hook.
  useSuppressAutofill();
  return (
    <Routes>
      {/* Standalone "popup" routes — opened in their own tab from elsewhere
          in the app. They keep classification banners but drop the navbar
          and toaster so the page fills the whole viewport. */}
      <Route element={<StandaloneLayout />}>
        <Route path="/table-viewer" element={<ProtectedRoute><RouteSuspense><TableViewerPage /></RouteSuspense></ProtectedRoute>} />
        <Route path="/output-viewer" element={<ProtectedRoute><RouteSuspense><OutputViewerPage /></RouteSuspense></ProtectedRoute>} />
        <Route path="/brewdb/results/:executionId" element={<ProtectedRoute><RouteSuspense><BrewDBResultsPage /></RouteSuspense></ProtectedRoute>} />
      </Route>

      {/* All other routes wrapped in the main layout (navbar, banners, notifications) */}
      <Route element={<AppLayout />}>

        {/* ── Public routes (no auth required) ──────────────────── */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/external" element={<ExternalRedirectPage />} />
        <Route path="/admin/setup" element={<RouteSuspense><AdminSetupPage /></RouteSuspense>} />

        {/* ── Error pages ───────────────────────────────────────── */}
        <Route path="/401" element={<ErrorPage code="401" />} />
        <Route path="/403" element={<ErrorPage code="403" />} />
        <Route path="/404" element={<ErrorPage code="404" />} />
        <Route path="/500" element={<ErrorPage code="500" />} />
        <Route path="/503" element={<ErrorPage code="503" />} />

        {/* ── Protected routes (require authentication) ─────────── */}

        {/* Dashboard */}
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        {/* Main Discover = the Editorial layout (formerly variant 1). */}
        <Route path="/dashboard/discover" element={<ProtectedRoute><DiscoverV1Editorial /></ProtectedRoute>} />
        <Route path="/dashboard/build" element={<Navigate to="/dashboard/discover" replace />} />

        {/* Model Builder — wrapped in RunViewGate which redirects mobile users to the run view */}
        <Route
          path="/build/:modelId"
          element={
            <ProtectedRoute>
              <RouteSuspense>
                <RunViewGate builder={<ModelBuilderPage />} />
              </RouteSuspense>
            </ProtectedRoute>
          }
        />
        <Route path="/build/:modelId/403" element={<ProtectedRoute><ModelForbiddenPage /></ProtectedRoute>} />
        <Route path="/build/:modelId/500" element={<ErrorPage code="500" />} />

        {/* Search redirect (legacy compatibility) */}
        <Route path="/search" element={<Navigate to="/dashboard/discover" replace />} />

        {/* Viz */}
        <Route path="/viz" element={<ProtectedRoute><RouteSuspense><VizPage /></RouteSuspense></ProtectedRoute>} />

        {/* BrewDB */}
        <Route path="/brewdb" element={<ProtectedRoute><RouteSuspense><BrewDBPage /></RouteSuspense></ProtectedRoute>} />

        {/* Learn / Help */}
        <Route path="/learn" element={<ProtectedRoute><RouteSuspense><LearnPage /></RouteSuspense></ProtectedRoute>} />
        <Route path="/learn/help/functionals" element={<ProtectedRoute><RouteSuspense><HelpFunctionalsPage /></RouteSuspense></ProtectedRoute>} />
        <Route path="/learn/help/functionals/:id" element={<ProtectedRoute><RouteSuspense><HelpFunctionalDetailPage /></RouteSuspense></ProtectedRoute>} />
        <Route path="/learn/help/models/:id" element={<ProtectedRoute><RouteSuspense><HelpModelDetailPage /></RouteSuspense></ProtectedRoute>} />

        {/* Developer (role-gated) */}
        <Route path="/develop" element={
          <ProtectedRoute requiredRoles={['developer', 'publisher', 'admin']}>
            <Navigate to="/develop/publish" replace />
          </ProtectedRoute>
        } />
        <Route path="/develop/publish" element={
          <ProtectedRoute requiredRoles={['developer', 'publisher', 'admin']}>
            <RouteSuspense><DevelopPublishPage /></RouteSuspense>
          </ProtectedRoute>
        } />
        <Route path="/develop/view/:jarId" element={
          <ProtectedRoute requiredRoles={['developer', 'publisher', 'admin']}>
            <RouteSuspense><DevelopViewPage /></RouteSuspense>
          </ProtectedRoute>
        } />

        {/* User Profile */}
        <Route path="/users/profile/:id" element={
          <ProtectedRoute><Navigate to="info" replace /></ProtectedRoute>
        } />
        <Route path="/users/profile/:id/:context" element={
          <ProtectedRoute><RouteSuspense><ProfilePage /></RouteSuspense></ProtectedRoute>
        } />

        {/* Admin profile detail — admin role gets the admin view; non-admins
            are redirected to the public profile route (guest view). */}
        <Route path="/admin/profiles/:id" element={
          <ProtectedRoute><AdminProfileDetailRoute /></ProtectedRoute>
        } />
        <Route path="/admin/profiles/:id/:context" element={
          <ProtectedRoute><AdminProfileDetailRoute /></ProtectedRoute>
        } />
        <Route path="/users/resource/:resourceId" element={
          <ProtectedRoute><RouteSuspense><ResourceViewPage /></RouteSuspense></ProtectedRoute>
        } />
        <Route path="/users/resource/:resourceId/403" element={<ErrorPage code="403" />} />

        {/* Groups workspace, and the /admin/groups redirects into it */}
        {groupsRoutes(
          <ProtectedRoute><RouteSuspense><GroupsWorkspace /></RouteSuspense></ProtectedRoute>,
        )}

        {/* Automation / Tasks */}
        <Route path="/tasks/:taskId" element={
          <ProtectedRoute><RouteSuspense><TaskViewPage /></RouteSuspense></ProtectedRoute>
        } />
        <Route path="/tasks/:taskId/:tab" element={
          <ProtectedRoute><RouteSuspense><TaskViewPage /></RouteSuspense></ProtectedRoute>
        } />

        {/* Admin (admin role required) */}
        <Route path="/admin" element={
          <ProtectedRoute requiredRoles={['admin']}>
            <Navigate to="/admin/dashboard" replace />
          </ProtectedRoute>
        } />
        <Route element={
          <ProtectedRoute requiredRoles={['admin']}>
            <RouteSuspense>
              <AdminLayout />
            </RouteSuspense>
          </ProtectedRoute>
        }>
          <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
          <Route path="/admin/configurations" element={<AdminConfigurationsPage />} />
          <Route path="/admin/profiles" element={<AdminProfilesPage />} />
          <Route path="/admin/credentials" element={<AdminCredentialsPage />} />
          <Route path="/admin/models" element={<AdminModelsPage />} />
          <Route path="/admin/models/:id" element={<AdminModelsPage />} />
          <Route path="/admin/logs" element={<AdminLogsPage />} />
          <Route path="/admin/resources" element={<AdminResourcesPage />} />
          <Route path="/admin/resources/:resourceId" element={<ResourceViewPage />} />
          <Route path="/admin/jobs" element={<AdminJobsPage />} />
          <Route path="/admin/tasks" element={<AdminTasksPage />} />
          <Route path="/admin/tasks/:taskId" element={<AdminTaskDetailPage />} />
          <Route path="/admin/tasks/:taskId/:tab" element={<AdminTaskDetailPage />} />
          <Route path="/admin/brewmasters" element={<AdminBrewmastersPage />} />
          <Route path="/admin/search" element={<AdminSearchPage />} />
          <Route path="/admin/sessions" element={<AdminSessionsPage />} />
          {/* Job detail lives under Profiles (Task 7/8 of
              admin-access-lifecycle-ui) — AccessLifecycleTab/LifecycleView's
              rows navigate here. The old standalone app-access and
              account-lifecycle pages/routes were retired in Task 8. */}
          <Route path="/admin/profiles/lifecycle/:jobId" element={<LifecycleJobDetailRoute />} />
          <Route path="/admin/registration" element={<AdminRegistrationPage />} />
          <Route path="/admin/brewai" element={<AdminBrewAiInsightsPage />} />
          <Route path="/admin/email-templates" element={<AdminEmailTemplatesPage />} />
        </Route>

        {/* Default redirect - matches legacy AngularJS 'otherwise' route */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<ErrorPage code="404" />} />
      </Route>
    </Routes>
  );
}
